/**
 * Is the editor we just embedded actually alive?
 *
 * WHY THIS EXISTS, measured. On k8s dev, opening a library component's editor and clicking
 * upstream's own Save produced ZERO POST/PUT/PATCH requests and no error of any kind; the
 * new display name was gone after a reload. The author types, saves, closes, and the edit
 * never existed.
 *
 * The cause was not in the editor. The v2 embed page references six stylesheets and eight
 * scripts as `http://` URLs while being served over `https` — the platform builds them from
 * `LMS_ROOT_URL` / `CMS_ROOT_URL`, and this deployment runs with `ENABLE_HTTPS=false` — so
 * the browser blocks all fourteen as mixed active content. Without `require.js` and
 * `commons.js` the XBlock runtime never initialises, and upstream's Save is literally
 * `<a class="button action-primary save-button" href="#">`: an anchor with no handler bound
 * to it. Measured in the frame: `hasRequire: undefined`, `hasXBlock: undefined`.
 *
 * THE DEPLOYMENT SETTING IS THE FIX. This module is not it, and does not try to be — see
 * docs/authoring-embed-https.md. What it fixes is the SILENCE. A Save that cannot possibly
 * work must say so instead of swallowing the author's work, and that has to be true after
 * the scheme is corrected too, because any future asset failure — a 404, a CSP, one tenant
 * left with a stale `http://` origin in its own SiteConfiguration — reproduces this exactly.
 *
 * BOTH SIGNALS ARE MEASURED FROM THE FRAME ITSELF, not inferred from configuration:
 *
 *   blocked   an asset the embed asked for over `http://` while this page is `https:`.
 *             Deterministic — the browser refuses those by specification, so there is no
 *             judgement in it and no false alarm to worry about. It is also the exact
 *             failure that is live today.
 *   runtime   the globals upstream's editor needs, still absent. Corroborating only, and
 *             deliberately NOT a verdict on its own: the caller waits for the embed to have
 *             said nothing for several seconds first, so a slow-but-working editor is never
 *             accused. A healthy embed posts `plugin.resize` as soon as its JS runs.
 *
 * Reading the frame's document is legitimate here and not a trick: both embeds are served
 * through our own origin (that is what lets the CMS session cookie reach them), so this is
 * same-origin DOM. If it ever is not, the probe says nothing rather than guessing.
 */

/** The globals upstream's editor page and the v2 embed both define when their JS runs. */
const RUNTIME_GLOBALS = ['require', 'XBlock'] as const;

export interface EmbedProbe {
  /**
   * Asset URLs the embed asked for insecurely, in document order. Non-empty means the
   * browser blocked them and nothing in that frame can be trusted to work.
   */
  blocked: string[];
  /** Which of `RUNTIME_GLOBALS` are still undefined in the frame. */
  missingRuntime: string[];
}

/**
 * What is wrong with this embed, or `null` if we cannot see into it.
 *
 * `null` is a deliberate third answer. A frame we cannot read is not a frame we can
 * accuse — that is the same "absence read as a finding" mistake in the other direction.
 */
export function inspectEmbed(frame: Window | null, pageProtocol: string): EmbedProbe | null {
  if (!frame) return null;

  let doc: Document | null = null;
  try {
    doc = frame.document;
  } catch {
    // Cross-origin. Nothing to say.
    return null;
  }
  if (!doc) return null;

  const blocked: string[] = [];
  // Mixed content is only blocked on a secure page; on plain HTTP — local development —
  // an http:// asset loads and there is no defect to report.
  if (pageProtocol === 'https:') {
    for (const node of doc.querySelectorAll('script[src], link[rel~="stylesheet"][href]')) {
      // The ATTRIBUTE, not the resolved `.src`/`.href` property. The property resolves a
      // relative path against the document's own base, which is https, so every relative
      // asset would look secure and the http:// ones would be the only thing this could
      // ever see — which happens to be right, and for the wrong reason.
      const url = node.getAttribute('src') ?? node.getAttribute('href') ?? '';
      if (/^http:\/\//i.test(url)) blocked.push(url);
    }
  }

  const missingRuntime = RUNTIME_GLOBALS.filter(
    (name) => (frame as unknown as Record<string, unknown>)[name] === undefined,
  );

  return { blocked, missingRuntime };
}

/**
 * The origins an operator has to fix, from the blocked URLs.
 *
 * The author does not need fourteen URLs; whoever fixes the deployment needs the hosts,
 * because those are what `LMS_ROOT_URL` and `CMS_ROOT_URL` name.
 */
export function blockedOrigins(blocked: string[]): string[] {
  const origins = new Set<string>();
  for (const url of blocked) {
    const match = /^http:\/\/([^/]+)/i.exec(url);
    if (match?.[1]) origins.add(`http://${match[1]}`);
  }
  return [...origins];
}
